import { HttpClientError } from '@/services/httpClient';

/**
 * Retry an async operation with exponential backoff.
 *
 * Retries network errors and HTTP 5xx; skips 4xx (auth, not-found, validation —
 * they will not succeed on a second try).
 */
export interface WithRetryOptions {
  attempts?: number;
  initialDelayMs?: number;
  maxDelayMs?: number;
}

function isRetryable(err: unknown): boolean {
  if (err instanceof HttpClientError) return err.status >= 500;
  return true;
}

export async function withRetry<T>(
  fn: () => Promise<T>,
  options: WithRetryOptions = {}
): Promise<T> {
  const attempts = options.attempts ?? 3;
  const initialDelay = options.initialDelayMs ?? 500;
  const maxDelay = options.maxDelayMs ?? 4000;

  let lastError: unknown;
  for (let attempt = 1; attempt <= attempts; attempt += 1) {
    try {
      return await fn();
    } catch (err) {
      lastError = err;
      if (attempt === attempts || !isRetryable(err)) break;
      const delay = Math.min(initialDelay * 2 ** (attempt - 1), maxDelay);
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }
  throw lastError;
}
